import { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { PlusCircle } from 'lucide-react';
import {
  calculateTotalCost,
  getCostBreakdown,
  type BreakdownSnapshot,
  type BreakdownDocumentContents,
} from '@jobos/shared';
import { Combobox } from '@/components/ui/combobox';
import { Panel } from '@/components/ui/panel';
import { DropdownButtonMenu } from '@/components/ui/dropdown-button-menu';
import { useSubItemCategoriesContext } from '@/contexts/SubItemCategoriesContext';
import { toFlatItems } from '@/pages/jobs/components/breakdown/types';
import type { LineItemData } from '@/pages/jobs/components/breakdown/LineItemList';
import type { SubItemForm } from '@/pages/jobs/components/breakdown/subItemTypes';
import { getSubItemTypeOptions } from '@/pages/jobs/components/breakdown/subItemTypes';
import { BreakdownDndWrapper } from '@/pages/jobs/components/breakdown/BreakdownDndWrapper';
import { BreakdownAddActions } from '@/pages/jobs/components/breakdown/BreakdownAddActions';
import { JobRatesDisplay } from '@/pages/jobs/components/breakdown/JobRatesDisplay';
import { LineItemDetail } from '@/pages/jobs/components/breakdown/LineItemDetail';
import { CostBreakdownSummary } from '@/pages/jobs/components/breakdown/CostBreakdownSummary';
import { CarpetLineItemBody } from '@/pages/jobs/components/carpet/CarpetLineItemBody';
import { EquipmentLineItemBody } from '@/pages/jobs/components/equipment/EquipmentLineItemBody';
import {
  snapshotToJobBreakdown,
  roundingFromSnapshot,
  flatItemsToContents,
} from '@/pages/jobs/components/breakdown/templateConversions';
import { useTemplateBreakdownState } from './useTemplateBreakdownState';

export interface EditorHandle {
  getContents: () => BreakdownDocumentContents;
  markSaved: () => void;
}

export interface TemplateBreakdownEditorProps {
  initialSnapshot: BreakdownSnapshot;
  projectType: string;
  onDirtyChange?: (dirty: boolean) => void;
  editorRef: React.Ref<EditorHandle>;
}

/**
 * In-settings editor for a job-type breakdown TEMPLATE (generic project types).
 *
 * Composes the existing job-breakdown LIST-MODE widgets over the stripped
 * `useTemplateBreakdownState` hook. Deliberately omits all job-only chrome
 * (version switcher, invoice/billing dialogs, undo/redo, copy/paste, export,
 * bulk-select, table/column view modes, preview mode) — this is list mode only.
 *
 * The component CONSUMES `SubItemCategoriesContext`; the page that mounts it is
 * responsible for providing it (and for re-mounting/keying by template id, so
 * the hook can seed once from the initial snapshot).
 */
export function TemplateBreakdownEditor({
  initialSnapshot,
  projectType: _projectType,
  onDirtyChange,
  editorRef,
}: TemplateBreakdownEditorProps) {
  const { categories, typeMeta } = useSubItemCategoriesContext();

  // Seed the hook once from the initial snapshot. The snapshot is the initial
  // value only — re-seeding on identity change is the page's concern (it keys
  // by template id). useMemo with [] keeps the seed stable across re-renders.
  const initialFlat = useMemo(
    () => toFlatItems(snapshotToJobBreakdown(initialSnapshot)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialRates = useMemo(
    () => ({
      taxRate: Number(initialSnapshot.taxRate ?? 0),
      markupRate: Number(initialSnapshot.markupRate ?? 0),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialRounding = useMemo(
    () => roundingFromSnapshot(initialSnapshot),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const editor = useTemplateBreakdownState(initialFlat, initialRates, initialRounding, typeMeta);

  // Live per-line-item display total (avoids stale cached totalCost on edits).
  const computeItemTotal = useCallback(
    (subItems: SubItemForm[]) => calculateTotalCost(subItems, editor.rates, typeMeta, categories),
    [editor.rates, typeMeta, categories],
  );

  // Notify the page of dirty transitions.
  useEffect(() => {
    onDirtyChange?.(editor.dirty);
  }, [editor.dirty, onDirtyChange]);

  // Imperative handle: serialize current editor state into the contents payload.
  useImperativeHandle(
    editorRef,
    () => ({
      getContents: () =>
        flatItemsToContents(editor.flatItems, editor.rates, editor.roundingPrecision),
      markSaved: editor.markSaved,
    }),
    [editor.flatItems, editor.rates, editor.roundingPrecision, editor.markSaved],
  );

  // Render the expanded line item body. Mirrors JobDetailBreakdown's
  // renderAccordionBody but uses the template hook's handlers. The body only
  // renders for the expanded (= selected) item, so handleSubItemsChange /
  // handleAddSubItem (which target selectedItemId) operate on the right row.
  const renderAccordionBody = useCallback(
    (item: LineItemData) => {
      if (item.category.toLowerCase() === 'disclaimer') return null;
      if (item.category === 'Carpet Cleaning') {
        // handleSubItemsChange targets the hook's selectedItemId; that is safe
        // here because this editor wires expandedItemId and selectedItemId to
        // the same value (expanded === selected), unlike JobDetailBreakdown.
        return (
          <CarpetLineItemBody
            subItems={item.subItems}
            onSubItemsChange={editor.handleSubItemsChange}
          />
        );
      }
      if (item.category === 'Equipment Rental') {
        return (
          <EquipmentLineItemBody
            subItems={item.subItems}
            onSubItemsChange={editor.handleSubItemsChange}
            lineItemDescription={item.description ?? null}
          />
        );
      }
      return (
        <div className="space-y-3">
          <div className="w-44">
            <Combobox
              value=""
              onValueChange={(value) => {
                if (value) editor.handleAddSubItem(value as string);
              }}
              options={getSubItemTypeOptions(categories)}
              placeholder={
                <span className="inline-flex items-center gap-1.5">
                  <PlusCircle className="h-4 w-4" />
                  Add item
                </span>
              }
              searchPlaceholder="Search..."
              emptyText="No type found."
            />
          </div>
          <LineItemDetail
            item={item}
            onSubItemsChange={editor.handleSubItemsChange}
            taxRate={editor.rates.taxRate}
            markupRate={editor.rates.markupRate}
            roundingPrecision={editor.roundingPrecision}
            onRemoveRounding={() => editor.handleSetRounding(null)}
            selectedSubItemId={editor.selectedSubItemId}
            onSelectSubItem={editor.setSelectedSubItemId}
          />
        </div>
      );
    },
    [categories, editor],
  );

  // Whole-template cost summary — aggregate every sub-item across all line items.
  // Intentionally NOT a mirror of JobDetailBreakdown (whose list mode uses a
  // plain total row): aggregating getCostBreakdown over all flattened sub-items
  // is correct because tax/markup/profit/discount/category totals are linear
  // sums, with rounding applied once on the grand total.
  const summaryBreakdown = useMemo(() => {
    const allSubItems = editor.flatItems
      .filter((i) => i.type === 'line-item')
      .flatMap((i) => (i.data as LineItemData).subItems);
    return getCostBreakdown(allSubItems, editor.rates, typeMeta, categories, editor.roundingPrecision);
  }, [editor.flatItems, editor.rates, editor.roundingPrecision, typeMeta, categories]);

  const hasItems = editor.flatItems.length > 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-3 mb-4">
        <JobRatesDisplay rates={editor.rates} onRatesChange={editor.handleRatesChange} />
        <DropdownButtonMenu
          label="Round Prices"
          ariaLabel="Round prices"
          disabled={!hasItems}
          items={[
            { label: 'Round to nearest $0.10', onClick: () => editor.handleSetRounding(0.1) },
            { label: 'Round to nearest $1.00', onClick: () => editor.handleSetRounding(1) },
          ]}
        />
      </div>

      <Panel
        title={
          <BreakdownAddActions
            onAddLineItem={editor.handleAddLineItem}
            onAddProject={editor.handleAddProject}
            checkedCount={0}
            // Template-item insertion and copy/paste are out of scope for the
            // template editor; suppress those affordances. onCopy/onPaste stay
            // as required no-ops (Copy is already hidden by checkedCount={0}).
            showTemplateItems={false}
            showPaste={false}
            onCopy={() => {}}
            onPaste={() => {}}
            isPreviewMode={false}
          />
        }
        className="flex-1 flex flex-col min-h-0"
        contentClassName="flex-1 min-h-0 space-y-4 overflow-auto pb-1"
      >
        <BreakdownDndWrapper
          flatItems={editor.flatItems}
          visibleSortableIds={editor.getVisibleSortableIds()}
          collapsedProjects={editor.collapsedProjects}
          selectedItemId={editor.selectedItemId}
          expandedItemId={editor.selectedItemId}
          onSelectItem={editor.handleToggleExpand}
          onDeleteLineItem={editor.handleDeleteLineItem}
          onCategoryChange={editor.handleLineItemCategoryChange}
          onDescriptionChange={editor.handleLineItemDescriptionChange}
          onItemDateChange={editor.handleLineItemDateChange}
          onDragEnd={editor.handleDragEnd}
          onProjectDragStart={editor.startProjectDrag}
          onProjectDragEnd={editor.handleProjectDragEnd}
          onMoveToEnd={editor.handleMoveToEnd}
          enableSectionDropZones
          onProjectDragCancel={editor.cancelProjectDrag}
          onToggleCollapse={editor.toggleCollapse}
          onRenameProject={editor.handleRenameProject}
          onDeleteProject={editor.handleDeleteProject}
          onAddLineItemToProject={editor.handleAddLineItemToProject}
          renderAccordionBody={renderAccordionBody}
          computeItemTotal={computeItemTotal}
          useRingHighlight
        />

        {hasItems && (
          <CostBreakdownSummary
            breakdown={summaryBreakdown}
            taxRate={editor.rates.taxRate}
            markupRate={editor.rates.markupRate}
            onRemoveRounding={
              editor.roundingPrecision ? () => editor.handleSetRounding(null) : undefined
            }
          />
        )}
      </Panel>
    </div>
  );
}
